import React, { useState, useEffect } from 'react';
import { makeStyles, tokens, Skeleton, SkeletonItem } from '@fluentui/react-components';
import {
  TextBulletListSquareRegular,
  CalendarRegular,
  EditRegular,
} from '@fluentui/react-icons';
import CopilotIcon from '../../../assets/office/ribbon/Copilot.svg';

type TopOfDocState = 'loading' | 'rest';

interface TopOfDocStyleDProps {
  onCopilotClick?: () => void;
  onPillClick?: (pillText: string) => void;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
    justifyContent: 'center',
    width: '816px', // Fixed to Slate editor width
    maxWidth: '816px',
    minWidth: '816px',
    padding: '0',
    boxSizing: 'border-box',
    margin: '0 auto', // Center in parent container
  },
  
  // Pill buttons from Style A
  pill: {
    flexShrink: 0,
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    padding: '10px 14px 10px 12px',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.1s ease',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1,
      boxShadow: tokens.shadow4,
      border: '1px solid transparent',
    },
  },
  
  // Icon styling
  icon: {
    width: '20px',
    height: '20px',
    flexShrink: 0,
    color: tokens.colorNeutralForeground2,
  },
  
  pillText: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: tokens.colorNeutralForeground2,
    whiteSpace: 'nowrap',
    fontFamily: '"Segoe UI", sans-serif',
  },
  
  // Copilot button from Style C - same style as DAB floating button
  copilotButton: {
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '12px',
    padding: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      boxShadow: tokens.shadow4,
      border: '1px solid transparent',
    },
  },
  
  // Inner button wrapper
  copilotInnerButton: {
    backgroundColor: 'transparent',
    borderRadius: '4px',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    cursor: 'pointer',
  },
  
  copilotIconImg: {
    width: '20px',
    height: '20px',
    flexShrink: 0,
  },
  
  // Skeleton styles
  skeletonPill: {
    flexShrink: 0,
    width: '200px',
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    padding: '10px 14px 10px 12px',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '12px',
    boxSizing: 'border-box',
  },
  
  skeletonContainer: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
  },
  
  skeletonIcon: {
    width: '20px',
    height: '20px',
    borderRadius: '4px',
  },
  
  skeletonText: {
    width: '80px',
    height: '16px',
    borderRadius: '4px',
  },
});

export const TopOfDocStyleD: React.FC<TopOfDocStyleDProps> = ({ 
  onCopilotClick,
  onPillClick,
}) => {
  const styles = useStyles();
  const [state, setState] = useState<TopOfDocState>('loading');

  // Loading state timer
  useEffect(() => {
    const loadingTimer = setTimeout(() => {
      setState('rest');
    }, 1000);

    return () => {
      clearTimeout(loadingTimer);
    };
  }, []);

  // Render skeleton pill
  const renderSkeletonPill = () => (
    <div className={styles.skeletonPill}>
      <Skeleton>
        <div className={styles.skeletonContainer}>
          <SkeletonItem className={styles.skeletonIcon} />
          <SkeletonItem className={styles.skeletonText} />
        </div>
      </Skeleton>
    </div>
  );

  // Loading state - all skeletons
  if (state === 'loading') {
    return (
      <div className={styles.container}>
        {/* Pill 0 skeleton */}
        {renderSkeletonPill()}
        
        {/* Pill 1 skeleton */}
        {renderSkeletonPill()}
        
        {/* Pill 2 skeleton */}
        {renderSkeletonPill()}
        
        {/* Copilot button - no loading state */}
        <div 
          className={styles.copilotButton}
          onClick={onCopilotClick}
        >
          <div className={styles.copilotInnerButton}>
            <img 
              src={CopilotIcon} 
              alt="Copilot" 
              className={styles.copilotIconImg}
            />
          </div>
        </div>
      </div>
    );
  }

  // Rest state - all loaded
  return (
    <div className={styles.container}>
      {/* Pill 0 - Summarize this document */}
      <div 
        className={styles.pill}
        onClick={() => onPillClick?.('Summarize this document')}
      >
        <TextBulletListSquareRegular className={styles.icon} />
        <span className={styles.pillText}>Summarize this document</span>
      </div>
      
      {/* Pill 1 - Prepare me for a meeting (from Style A) */}
      <div 
        className={styles.pill}
        onClick={() => onPillClick?.('Prepare me for a meeting')}
      >
        <CalendarRegular className={styles.icon} />
        <span className={styles.pillText}>Prepare me for a meeting</span>
      </div>
      
      {/* Pill 2 - Get writing suggestions (from Style A) */}
      <div 
        className={styles.pill}
        onClick={() => onPillClick?.('Get writing suggestions')}
      >
        <EditRegular className={styles.icon} />
        <span className={styles.pillText}>Get writing suggestions</span>
      </div>
      
      {/* Copilot button */}
      <div 
        className={styles.copilotButton}
        onClick={onCopilotClick}
      >
        <div className={styles.copilotInnerButton}>
          <img 
            src={CopilotIcon} 
            alt="Copilot" 
            className={styles.copilotIconImg}
          />
        </div>
      </div>
    </div>
  );
};

export default TopOfDocStyleD;
